import React, { useEffect, useState } from "react";
import { Accessibility, X } from "lucide-react";
import { useAttentionSettings, setAttentionSetting } from "@/lib/attentionSettings";
import { cn } from "@/lib/utils";

/** Applies the accessibility settings as classes on <html> so every page responds. */
export function useApplyAccessibility() {
  const s = useAttentionSettings();
  useEffect(() => {
    const h = document.documentElement;
    h.classList.toggle("a11y-dyslexia", !!s.dyslexiaFont);
    h.classList.toggle("a11y-text-large", s.textSize === "large");
    h.classList.toggle("a11y-text-xl", s.textSize === "xl");
    h.classList.toggle("a11y-spacing", s.lineSpacing === "relaxed");
    h.classList.toggle("a11y-contrast", s.contrast === "high");
    h.classList.toggle("a11y-lowstim", s.contrast === "lowstim");
    h.classList.toggle("a11y-reduced-motion", !!s.reducedMotion);
    h.classList.toggle("a11y-low-distraction", !!s.lowDistraction);
  }, [s]);
  return s;
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={cn("w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2 rounded-xl border-2 border-slate-900 text-left text-sm font-bold transition-colors",
        checked ? "bg-emerald-200" : "bg-white hover:bg-yellow-50")}>
      <span>{label}</span>
      <span aria-hidden className={cn("h-6 w-11 rounded-full border-2 border-slate-900 relative transition-colors", checked ? "bg-slate-900" : "bg-slate-200")}>
        <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white border border-slate-900 transition-transform", checked ? "translate-x-5" : "translate-x-0.5")} />
      </span>
    </button>
  );
}

function Choice({ label, value, options, onChange, testid }) {
  return (
    <div>
      <p className="text-xs font-bold text-slate-600 mb-1">{label}</p>
      <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
            className={cn("min-h-[44px] rounded-xl border-2 border-slate-900 text-xs font-bold px-1", value === o.value ? "bg-slate-900 text-white" : "bg-white hover:bg-yellow-50")}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Persistent "Make this work for me" control — mounted once in Layout. */
export default function AccessibilityPanel() {
  const s = useApplyAccessibility();
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => setAttentionSetting(k, v);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey); window.addEventListener("mwq:a11y-open", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mwq:a11y-open", onOpen); };
  }, []);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="a11y-open" aria-haspopup="dialog" aria-expanded={open}
        className="hidden lg:inline-flex fixed left-3 bottom-4 z-40 items-center justify-center gap-2 h-11 w-11 sm:w-auto px-0 sm:px-3 rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] font-bold text-sm hover:-translate-y-0.5 transition-transform focus-visible:ring-2 focus-visible:ring-amber-500">
        <Accessibility size={18} strokeWidth={2.5} aria-hidden /> <span className="hidden sm:inline">Make this work for me</span><span className="sr-only sm:hidden">Make this work for me</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="a11y-title" onClick={(e) => e.stopPropagation()} data-testid="a11y-panel"
            className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-3xl border-4 border-slate-900 bg-[#fff8e7] p-4 shadow-[0_6px_0_0_rgba(15,23,42,1)]">
            <div className="flex items-center justify-between mb-3">
              <h2 id="a11y-title" className="font-heading text-lg md:text-xl font-bold">Make this work for me</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="a11y-close" className="h-11 w-11 inline-flex items-center justify-center rounded-full border-2 border-slate-900 bg-white"><X size={18} /></button>
            </div>
            <div className="grid gap-2">
              <Toggle label="Dyslexia-friendly font" checked={s.dyslexiaFont} onChange={set("dyslexiaFont")} testid="a11y-dyslexia" />
              <Choice label="Text size" value={s.textSize} onChange={set("textSize")} testid="a11y-text" options={[{ value: "normal", label: "Standard" }, { value: "large", label: "Large" }, { value: "xl", label: "Extra large" }]} />
              <Choice label="Line spacing" value={s.lineSpacing} onChange={set("lineSpacing")} testid="a11y-spacing" options={[{ value: "normal", label: "Standard" }, { value: "relaxed", label: "Relaxed" }]} />
              <Choice label="Colours" value={s.contrast} onChange={set("contrast")} testid="a11y-contrast" options={[{ value: "normal", label: "Warm" }, { value: "high", label: "High contrast" }, { value: "lowstim", label: "Calm" }]} />
              <Toggle label="Reduce motion" checked={s.reducedMotion} onChange={set("reducedMotion")} testid="a11y-motion" />
              <Toggle label="Fewer distractions (hide extras)" checked={s.lowDistraction} onChange={set("lowDistraction")} testid="a11y-distraction" />
              <Toggle label="Show me (pictures first, less reading)" checked={s.showMe} onChange={set("showMe")} testid="a11y-showme" />
              <Choice label="Reading level" value={s.readingLevel} onChange={set("readingLevel")} testid="a11y-reading" options={[{ value: "simpler", label: "Simpler" }, { value: "standard", label: "Standard" }, { value: "stretch", label: "Stretch" }]} />
              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">Read-aloud speed</p>
                <input type="range" min="0.6" max="1.4" step="0.1" value={s.readAloudRate} onChange={(e) => set("readAloudRate")(Number(e.target.value))} aria-label="Read-aloud speed" data-testid="a11y-rate" className="w-full h-11" />
              </div>
              <div>
                <label htmlFor="a11y-lang" className="text-xs font-bold text-slate-600 mb-1 block">Key words in my home language</label>
                <select id="a11y-lang" value={s.glossaryLang} onChange={(e) => set("glossaryLang")(e.target.value)} data-testid="a11y-lang" className="w-full min-h-[44px] rounded-xl border-2 border-slate-900 bg-white px-3 font-bold text-sm">
                  <option value="">English only</option><option value="es">Español</option><option value="fr">Français</option><option value="ar">العربية</option><option value="hi">हिन्दी</option><option value="ur">اردو</option><option value="pl">Polski</option><option value="zh">中文</option>
                </select>
              </div>
              <p className="text-[11px] text-slate-600 mt-1">Saved on this device only. Nothing here is ever behind a payment.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
